// @ts-nocheck
import _ from "lodash";
import moment from "moment";
import Papa from 'papaparse';

type MathMethod = 'round' | 'floor' | 'ceil';

/**
 * Decimal adjustment of a number.
 *
 * @param type The type of adjustment.
 * @param value The number.
 * @param exp The exponent (the 10 logarithm of the adjustment base).
 * @returns The adjusted value.
 */
function decimalAdjust(type: MathMethod, value: number, exp: number): number {
  // If the exp is undefined or zero...
  if (typeof exp === "undefined" || +exp === 0) {
    return Math[type](value);
  }
  let adjustedValue = +value;
  const adjustedExp = +exp;
  // If the value is not a number or the exp is not an integer...
  if (isNaN(adjustedValue) || !(typeof adjustedExp === "number" && adjustedExp % 1 === 0)) {
    return NaN;
  }
  // Shift
  let valueParts = adjustedValue.toString().split("e");
  adjustedValue = Math[type](+(valueParts[0] + "e" + (valueParts[1] ? +valueParts[1] - adjustedExp : -adjustedExp)));
  // Shift back
  valueParts = adjustedValue.toString().split("e");
  return +(valueParts[0] + "e" + (valueParts[1] ? +valueParts[1] + adjustedExp : adjustedExp));
}

interface RegistrationTeam {
  teamName?: string;
  info?: Record<string, unknown>;
}

interface DivisionTeam {
  name?: string;
  info?: Record<string, unknown>;
}

interface Helpers {
  jsonToQueryString: (json?: Record<string, string | number | boolean>) => string;
  convertNumbersToStrings: <T extends Record<string, unknown>>(data: T) => T;
  convertToUSD: (amount: number) => string;
  convertJSONToCSV: (json?: unknown[], filename?: string, modalId?: string) => void;
  isRegistrationTeamUpdated: (registrationTeam: RegistrationTeam, divisionTeam: DivisionTeam, players?: unknown[]) => boolean;
  getLeagueNameFromPath: (pathname?: string) => string;
  roundDecimal: (value: number, exp: number) => number;
  parseDate: (strDate?: string, delimiter?: string) => Date;
  queryStringToJSON: (str?: string) => Record<string, string>;
  doesKeyExistInList: <T extends Record<string, unknown>>(list?: T[], key?: string) => boolean;
  serverToView: (value?: string | boolean) => string | undefined;
  viewToServer: (value?: string) => boolean | string;
  getObjId: <T extends { _id?: string }>(item: T | string | null | undefined) => string | undefined;
}

const helpers: Helpers = {
  jsonToQueryString(json: Record<string, string | number | boolean> = {}): string {
    return Object.keys(json)
      .map(function(key) {
        return encodeURIComponent(key) + "=" + encodeURIComponent(json[key]);
      })
      .join("&");
  },

  convertNumbersToStrings<T extends Record<string, unknown>>(data: T): T {
    const obj = { ...data } as Record<string, unknown>;
    // Check if the input is an object
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      // Iterate over each key in the object
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          // Check if the value is a number
          if (typeof obj[key] === 'number') {
            obj[key] = (obj[key] as number).toString();
          } else if (typeof obj[key] === 'object') {
            // Recursively handle nested objects
            obj[key] = this.convertNumbersToStrings(obj[key] as Record<string, unknown>);
          }
        }
      }
    }
    return obj as T;
  },

  convertToUSD(amount: number): string {
    return (amount / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  },

  convertJSONToCSV(json: unknown[] = [], filename: string = "export.csv", modalId: string = ""): void {
    const csvContent = Papa.unparse(json);
    const download = function(content: string, fileName: string, mimeType?: string) {
      const a = document.createElement('a');
      mimeType = mimeType || 'application/octet-stream';

      if (URL && 'download' in a) { //html5 A[download]
        a.href = URL.createObjectURL(new Blob([content], {
          type: mimeType
        }));
        a.setAttribute('download', fileName);
        if (modalId) {
          const modalElement = document.getElementById(modalId);
          if (modalElement) {
            modalElement.appendChild(a);
            a.click();
            modalElement.removeChild(a);
          }
        } else {
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } else {
        window.location.href = 'data:application/octet-stream,' + encodeURIComponent(content); // only this mime type is supported
      }
    };

    download(csvContent, filename, 'text/csv;encoding:utf-8');
  },

  isRegistrationTeamUpdated(registrationTeam: RegistrationTeam, divisionTeam: DivisionTeam, players?: unknown[]): boolean {
    if (!_.get(registrationTeam, 'teamName') || !_.get(divisionTeam, 'name')) {
      return false;
    }
    return registrationTeam.teamName !== _.get(divisionTeam, 'name') ||
      _.isEqual(_.get(registrationTeam, 'info'), _.get(divisionTeam, 'info'));
  },

  getLeagueNameFromPath(pathname: string = '/'): string {
    const leagueName = pathname.split("/")[1];
    return leagueName;
  },

  roundDecimal(value: number, exp: number): number {
    return decimalAdjust("round", value, exp);
  },

  parseDate(strDate: string = "", delimiter: string = ""): Date {
    const [month, day, year] = strDate.split(delimiter);
    const dateObj = new Date();
    dateObj.setMonth(parseInt(month) - 1);
    dateObj.setDate(parseInt(day));
    dateObj.setFullYear(parseInt(year));
    return dateObj;
  },

  queryStringToJSON(str: string = ""): Record<string, string> {
    const pairs = str.split("&");
    const result: Record<string, string> = {};
    pairs.forEach(function(pair) {
      const [key, value] = pair.split("=");
      result[key] = decodeURIComponent(value || "");
    });

    return JSON.parse(JSON.stringify(result));
  },

  doesKeyExistInList<T extends Record<string, unknown>>(list: T[] = [], key: string = ""): boolean {
    const result = _.filter(list, item => {
      return !!item[key];
    });
    return !!result.length;
  },

  serverToView(value: string | boolean = ""): string | undefined {
    // parameter should be a primitive
    let result: string | undefined;
    if (typeof value === "boolean") {
      result = value ? "Yes" : "No";
    } else if (typeof value === "string") {
      result = value;
    }
    return result;
  },

  viewToServer(value: string = ""): boolean | string {
    let result: boolean | string;
    if (value === "Yes" || value === "No") {
      result = value === "Yes";
    } else {
      result = value;
    }
    return result;
  },

  getObjId<T extends { _id?: string }>(item: T | string | null | undefined): string | undefined {
    return typeof item === "object" && item ? item._id : (item as string | undefined);
  }
};

export default helpers;
